/**
 * Reset Password Screen
 * Allows users to set a new password after clicking the reset link from email
 * Redesigned with pill-shaped inputs to match auth-screens-preview
 */

import React, { useState, useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import {
  Box,
  VStack,
  Text,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { colors, FS_SM, FS_MD } from '../../theme';
import { useAuthStore } from '../../store/authStore';

import { E2E_MODE } from '../../config/constants';
import { PillInput } from '../../components/auth/PillInput';
import { PillButton } from '../../components/auth/PillButton';
import { PasswordStrength } from '../../components/auth/PasswordStrength';
import { TextLinkButton } from '../../components/ui';
import { useAppBannerStore } from '../../hooks/useAppBanner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

const FS_HEADING = 28;


export interface ResetPasswordScreenDisplayProps {
  password: string;
  confirmPassword: string;
  isLoading: boolean;
  error: string | null;
  touched: { password: boolean; confirmPassword: boolean };
  validation: { password: boolean; confirmPassword: boolean };
  isFormValid: boolean;
  onPasswordChange: (text: string) => void;
  onConfirmPasswordChange: (text: string) => void;
  onBlur: (field: 'password' | 'confirmPassword') => void;
  onSubmit: () => void;
  onBackToLogin: () => void;
  isE2EMode?: boolean;
}

export function ResetPasswordScreenDisplay({
  password,
  confirmPassword,
  isLoading,
  error,
  touched,
  validation,
  isFormValid,
  onPasswordChange,
  onConfirmPasswordChange,
  onBlur,
  onSubmit,
  onBackToLogin,
  isE2EMode,
}: ResetPasswordScreenDisplayProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: theme.background }}
    >
      <ScrollView
        contentContainerStyle={{ flexGrow: 1 }}
        keyboardShouldPersistTaps="handled"
      >
        <Box flex={1} justifyContent="center" alignItems="center" px="$6">
          {/* Heading */}
          <VStack space="sm" mb="$8" alignItems="center">
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_HEADING * fontScale,
                fontWeight: '700',
                color: theme.text,
                ...ffs
              }}
            >
              {t('auth.resetPassword.title')}
            </Text>
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_MD * fontScale,
                color: theme.textSecondary,
                textAlign: 'center',
                lineHeight: 22 * fontScale,
                ...ffs
              }}
            >
              {t('auth.resetPassword.subtitle')}
            </Text>
          </VStack>

          {/* Form */}
          <Box w="$full" maxWidth={400}>
            <PillInput
              label={t('auth.resetPassword.newPasswordLabel')}
              icon={'\u{1F512}'}
              isPassword
              forceShowPassword={isE2EMode}
              testID="reset-input-password"
              errorTestID="reset-text-password-error"
              accessibilityLabel={t('auth.resetPassword.passwordAccessibility')}
              placeholder={'\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
              value={password}
              onChangeText={onPasswordChange}
              onBlur={() => onBlur('password')}
              editable={!isLoading}
              textContentType={isE2EMode ? 'none' : 'newPassword'}
              autoComplete={isE2EMode ? 'off' : 'new-password'}
              isInvalid={touched.password && !validation.password}
              errorText={t('auth.resetPassword.newPasswordError')}
            />
            <PasswordStrength password={password} />

            <View style={{ height: 8 }} />

            <PillInput
              label={t('auth.resetPassword.confirmPasswordLabel')}
              icon={'\u{1F512}'}
              isPassword
              forceShowPassword={isE2EMode}
              testID="reset-input-confirm"
              errorTestID="reset-text-confirm-error"
              accessibilityLabel={t('auth.resetPassword.confirmPasswordAccessibility')}
              placeholder={'\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
              value={confirmPassword}
              onChangeText={onConfirmPasswordChange}
              onBlur={() => onBlur('confirmPassword')}
              editable={!isLoading}
              textContentType={isE2EMode ? 'none' : 'newPassword'}
              autoComplete={isE2EMode ? 'off' : 'new-password'}
              isInvalid={touched.confirmPassword && !validation.confirmPassword}
              errorText={t('auth.resetPassword.confirmPasswordError')}
            />

            <PillButton
              testID="reset-button-submit"
              title={t('auth.resetPassword.updateButton')}
              onPress={onSubmit}
              isLoading={isLoading}
              isDisabled={isLoading || !isFormValid}
            />

            {/* Error Display */}
            {error && (
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: colors.error, textAlign: 'center', marginTop: 8, ...ffs }}>
                {error}
              </Text>
            )}

            {/* Back to Login Link */}
            <TextLinkButton testID="reset-link-login" accessibilityLabel={t('auth.resetPassword.backToLoginAccessibility')} accessibilityRole="link" label={t('auth.resetPassword.backToLogin')} onPress={onBackToLogin} disabled={isLoading} style={{ alignSelf: 'center', marginTop: 16 }} />
          </Box>
        </Box>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

export function ResetPasswordScreen() {
  const navigation = useNavigation();
  const { t } = useTranslation();
  const { updatePassword, isLoading, error, clearError } = useAuthStore();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false,
  });

  const validation = useMemo(() => {
    return {
      password: password.length >= 8,
      confirmPassword: password === confirmPassword && confirmPassword.length > 0,
    };
  }, [password, confirmPassword]);

  const isFormValid = validation.password && validation.confirmPassword;
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const handleResetPassword = async () => {
    setTouched({ password: true, confirmPassword: true });

    if (!isFormValid) {
      return;
    }

    try {
      await updatePassword(password);
      setShowSuccessDialog(true);
    } catch (err: unknown) {
      useAppBannerStore.getState().showBanner({ message: (err as Error).message });
    }
  };

  const handleBlur = (field: 'password' | 'confirmPassword') => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleBackToLogin = () => {
    navigation.reset({ index: 0, routes: [{ name: 'Login' as never }] });
  };

  // Clear error when inputs change
  // Intentional: only react to input changes, not error/clearError
  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (error) clearError();
  }, [password, confirmPassword]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // E2E mode
  const isE2EMode = E2E_MODE;

  return (
    <>
      <ResetPasswordScreenDisplay
        password={password}
        confirmPassword={confirmPassword}
        isLoading={isLoading}
        error={error}
        touched={touched}
        validation={validation}
        isFormValid={isFormValid}
        onPasswordChange={setPassword}
        onConfirmPasswordChange={setConfirmPassword}
        onBlur={handleBlur}
        onSubmit={handleResetPassword}
        onBackToLogin={handleBackToLogin}
        isE2EMode={isE2EMode}
      />
      <ConfirmDialog
        visible={showSuccessDialog}
        title={t('auth.resetPassword.successTitle')}
        message={t('auth.resetPassword.successMessage')}
        confirmLabel={t('common.ok')}
        confirmOnly
        onConfirm={() => {
          setShowSuccessDialog(false);
          navigation.reset({
            index: 0,
            routes: [{ name: 'Login' as never }],
          });
        }}
        onCancel={() => setShowSuccessDialog(false)}
      />
    </>
  );
}
